import { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { TrendingUp, RefreshCw, ShieldAlert, ArrowRight, Loader2, Zap, Target, LineChart, CheckCircle2, Star } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { FAQSection } from '@/components/FAQSection';
import { ThemeToggle } from '@/components/ThemeToggle';
import logo from '@/assets/logo.png';
import { motion, useInView } from 'framer-motion';
import { 
  generateSoftwareApplicationSchema, 
  generateOrganizationSchema,
  generateFAQSchema,
  SITE_URL 
} from '@/lib/structuredData';

// Framer Motion variants for premium animations
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] as const } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const } }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] as const } }
};

const Index = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const statsRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const isFeaturesInView = useInView(featuresRef, { once: true, margin: "-100px" });
  const isStatsInView = useInView(statsRef, { once: true, margin: "-100px" });

  // FAQ data for schema
  const faqData = [
    { question: "What is Sentinel?", answer: "Sentinel is an AI-powered financial intelligence platform that automates reconciliation, prevents duplicate payments, and detects invoice fraud with 99.2% accuracy." },
    { question: "How does duplicate payment detection work?", answer: "Our AI analyzes invoice patterns, vendor histories, amounts, and dates to identify potential duplicates before payment is made, saving companies millions in erroneous payments." },
    { question: "Does Sentinel integrate with Tally?", answer: "Yes, Sentinel seamlessly integrates with Tally ERP via CSV import, allowing you to reconcile Tally ledger entries with bank statements automatically." },
    { question: "What is the pricing for Sentinel?", answer: "Sentinel offers a free tier with 100 transactions/month, Starter at ₹999/month for 500 transactions, and Pro at ₹2,499/month for unlimited transactions with advanced features." },
    { question: "How accurate is the matching algorithm?", answer: "Sentinel achieves 99.2% matching accuracy using multi-factor confidence scoring that considers amount, date, narration similarity, and vendor identity." }
  ];

  // Multiple structured data schemas
  const structuredData = [
    generateSoftwareApplicationSchema({ free: true, price: 0 }),
    generateOrganizationSchema(),
    generateFAQSchema(faqData)
  ];

  useEffect(() => {
    if (user && !loading) {
      navigate('/inbox');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const stats = [
    { value: '99.2%', label: 'Matching Accuracy' },
    { value: '< 2min', label: 'Processing Time' },
    { value: '₹0', label: 'Setup Cost' },
    { value: '24/7', label: 'Autonomous Monitoring' },
  ];

  const features = [
    {
      icon: RefreshCw,
      title: 'Intelligent Reconciliation',
      description: 'Advanced matching algorithm automatically pairs bank transactions with ledger entries using multi-factor confidence scoring',
      badge: 'Automated'
    },
    {
      icon: ShieldAlert,
      title: 'Fraud Detection',
      description: 'Sophisticated models analyze invoice patterns, vendor histories, and payment anomalies to flag suspicious transactions',
      badge: 'Real-Time'
    },
    {
      icon: TrendingUp,
      title: 'Predictive Analytics',
      description: 'Forecast cash flow with precision using historical data patterns, seasonal trends, and predictive modeling',
      badge: 'Pro Feature'
    },
  ];

  const testimonials = [
    {
      quote: 'Sentinel prevented ₹2.3 crore in duplicate payments in just 3 months. The ROI is incredible.',
      author: 'Rajesh Kumar',
      role: 'CFO, TechCorp India',
      rating: 5
    },
    {
      quote: 'Reconciliation that used to take our team 5 days now completes in under an hour. Game changer.',
      author: 'Priya Sharma',
      role: 'Finance Director, Manufacturing Co',
      rating: 5
    },
  ];

  return (
    <>
      <SEO 
        title="Sentinel - Financial Intelligence Platform | Prevent Duplicate Payments"
        description="Stop duplicate payments and automate reconciliation. Enterprise-grade financial intelligence trusted by India's leading companies. 99.2% accuracy, <2min processing."
        canonical="https://yoursite.com"
        structuredData={structuredData}
      />
      <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
        {/* Premium Background Effects */}
        <div className="absolute inset-0 bg-gradient-hero" aria-hidden="true" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] animate-float" aria-hidden="true" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[100px] animate-float" style={{ animationDelay: '-3s' }} aria-hidden="true" />
        
        {/* Navigation */}
        <nav className="relative z-10 container mx-auto px-4 sm:px-6 py-4 sm:py-6" aria-label="Main navigation">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <img src={logo} alt="SENTINEL Logo" className="w-8 h-8 sm:w-10 sm:h-10 brightness-0 dark:brightness-100 dark:invert" />
              <span className="text-xl sm:text-2xl font-bold tracking-tight">SENTINEL</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <ThemeToggle />
              <Button onClick={() => navigate('/auth')} size="lg" className="shadow-premium rounded-2xl px-4 sm:px-6">
                Sign In <ArrowRight className="ml-2" size={16} aria-hidden="true" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <header ref={heroRef} className="relative z-10 container mx-auto px-6 pt-12 pb-16 md:pt-20 md:pb-20">
          <motion.div 
            className="max-w-5xl mx-auto text-center space-y-8"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-elevated border border-primary/30 text-primary-dark dark:text-primary text-sm font-semibold backdrop-blur-xl shadow-glow" 
              role="status"
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              Trusted by India's Leading Enterprises
            </motion.div>

            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[1.2] bg-gradient-primary bg-clip-text text-transparent pb-2"
            >
              Financial Intelligence<br />That Pays For Itself
            </motion.h1>

            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            >
              Eliminate duplicate payments, automate reconciliation, and forecast cash flow with enterprise-grade precision. Built for finance teams who demand accuracy.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center pt-6"
            >
              <Button 
                size="lg" 
                onClick={() => navigate('/auth')} 
                className="text-lg px-10 h-14 shadow-premium hover:shadow-glow-lg transition-all duration-300 rounded-2xl shine-effect active-press" 
              >
                Start Free Trial <ArrowRight className="ml-2" size={20} aria-hidden="true" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                onClick={() => navigate('/features')} 
                className="text-lg px-10 h-14 border-border/50 hover:border-primary/50 hover:bg-surface-elevated transition-all duration-300 rounded-2xl active-press" 
              >
                Explore Features
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div 
              ref={statsRef}
              variants={staggerContainer}
              initial="hidden"
              animate={isStatsInView ? "visible" : "hidden"}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16" 
              role="region" 
              aria-label="Key performance metrics"
            >
              {stats.map((stat, i) => (
                <motion.div 
                  key={i} 
                  variants={scaleIn}
                  className="space-y-2 hover-lift"
                >
                  <div className="text-3xl md:text-4xl font-bold text-primary" aria-label={`${stat.value} ${stat.label}`}>{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </header>

        {/* Features Section */}
        <section ref={featuresRef} className="relative z-10 container mx-auto px-6 pt-8 pb-16" aria-labelledby="features-heading">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial="hidden"
            animate={isFeaturesInView ? "visible" : "hidden"}
            variants={fadeInUp}
          >
            <h2 id="features-heading" className="text-4xl md:text-5xl font-bold mb-4">
              Enterprise Features.<br />Startup Simplicity.
            </h2>
            <p className="text-xl text-muted-foreground">
              Everything you need to transform your financial operations, nothing you don't.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto"
            initial="hidden"
            animate={isFeaturesInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            {features.map((feature, i) => (
              <motion.article
                key={i}
                variants={scaleIn}
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="group p-8 rounded-3xl bg-surface-elevated border border-border/50 hover:border-primary/50 hover:bg-surface-hover transition-all duration-500 hover:shadow-card-hover tilt-card"
              >
                <div className="flex items-start justify-between mb-6">
                  <motion.div 
                    className="w-14 h-14 bg-gradient-primary rounded-2xl flex items-center justify-center shadow-glow group-hover:shadow-glow-lg transition-all duration-500" 
                    aria-hidden="true"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                  >
                    <feature.icon className="w-7 h-7 text-white" aria-label={`${feature.title} icon`} />
                  </motion.div>
                  <span className="text-xs font-bold px-3 py-1 rounded-full bg-primary/15 text-primary-dark dark:text-primary border border-primary/20" role="status">
                    {feature.badge}
                  </span>
                </div>
                <h3 className="text-2xl font-bold mb-3 text-foreground">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
              </motion.article>
            ))}
          </motion.div>
        </section>

        {/* How It Works */}
        <section className="relative z-10 container mx-auto px-6 pt-8 pb-16" aria-labelledby="workflow-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="workflow-heading" className="text-4xl md:text-5xl font-bold mb-4">From Data to Insights in Minutes</h2>
              <p className="text-xl text-muted-foreground">Simple workflow, powerful results</p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Zap, title: 'Import Data', desc: 'CSV, XML, or API', alt: 'Lightning fast data import icon' },
                { icon: Target, title: 'Analyze', desc: 'Automatic matching', alt: 'Target analysis icon' },
                { icon: LineChart, title: 'Review Insights', desc: 'Confidence scoring', alt: 'Data insights and analytics icon' },
                { icon: CheckCircle2, title: 'Take Action', desc: 'One-click approval', alt: 'Quick approval action icon' },
              ].map((step, i) => (
                <div key={i} className="relative">
                  <div className="text-center space-y-4">
                    <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-2xl flex items-center justify-center shadow-glow" aria-hidden="true">
                      <step.icon className="w-8 h-8 text-white" aria-label={step.alt} />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold">{step.title}</h3>
                      <p className="text-muted-foreground text-sm">{step.desc}</p>
                    </div>
                  </div>
                  {i < 3 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-primary/50 to-transparent" aria-hidden="true"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="relative z-10 container mx-auto px-6 py-20" aria-labelledby="testimonials-heading">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="testimonials-heading" className="text-4xl md:text-5xl font-bold mb-4">Trusted by Finance Leaders</h2>
              <p className="text-xl text-muted-foreground">Real results from real companies</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {testimonials.map((item, i) => (
                <article key={i} className="p-8 rounded-3xl bg-surface-elevated border border-border/50 space-y-6">
                  <div className="flex gap-1" role="img" aria-label={`${item.rating} out of 5 stars`}>
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-primary text-primary" aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="text-lg text-foreground leading-relaxed">"{item.quote}"</blockquote>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center shadow-glow" aria-hidden="true">
                      <span className="text-white font-bold text-lg">{item.author[0]}</span>
                    </div>
                    <div>
                      <cite className="font-bold text-foreground not-italic">{item.author}</cite>
                      <div className="text-sm text-muted-foreground">{item.role}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 container mx-auto px-6 py-20 mb-20" aria-labelledby="cta-heading">
          <div className="max-w-5xl mx-auto relative">
            <div className="relative p-12 md:p-16 rounded-3xl bg-gradient-primary overflow-hidden shadow-premium">
              <div className="absolute inset-0 bg-gradient-shine animate-shimmer" aria-hidden="true"></div>
              <div className="relative z-10 text-center space-y-6">
                <h2 id="cta-heading" className="text-4xl md:text-5xl font-bold text-white">
                  Stop Losing Money to Manual Processes
                </h2>
                <p className="text-xl text-white/90 max-w-2xl mx-auto">
                  Join 500+ finance teams who've automated their reconciliation and saved millions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Button size="lg" onClick={() => navigate('/auth')} className="text-lg px-10 h-14 bg-white text-foreground hover:bg-white/90 shadow-premium rounded-2xl">
                    Start Free Trial <ArrowRight className="ml-2" size={20} aria-hidden="true" />
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => navigate('/support')} className="text-lg px-10 h-14 border-white text-white hover:bg-white/10 rounded-2xl active-press">
                    Talk to Sales
                  </Button>
                </div>
                <p className="text-sm text-white/80">
                  No credit card required • 14-day free trial • Cancel anytime
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQSection />

        {/* Footer */}
        <footer className="relative z-10 border-t border-border/50 bg-surface/50 backdrop-blur-xl" role="contentinfo">
          <div className="container mx-auto px-6 py-16">
            <div className="grid md:grid-cols-4 gap-12 mb-12">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img src={logo} alt="SENTINEL Logo" className="w-8 h-8 brightness-0 dark:brightness-100 dark:invert" />
                  <span className="text-xl font-bold">SENTINEL</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Financial Intelligence That Pays For Itself
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Product</h3>
                <nav className="space-y-3">
                  <Link to="/features" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Features
                  </Link>
                  <Link to="/pricing" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Pricing
                  </Link>
                  <Link to="/blog" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Blog
                  </Link>
                </nav>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Resources</h3>
                <nav className="space-y-3">
                  <Link to="/documentation" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Documentation
                  </Link>
                  <Link to="/api-reference" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    API Reference
                  </Link>
                  <Link to="/support" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Support
                  </Link>
                </nav>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Company</h3>
                <nav className="space-y-3">
                  <Link to="/about" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    About
                  </Link>
                  <Link to="/privacy" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </nav>
              </div>
            </div>
            <div className="border-t border-border/50 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © 2025 Sentinel. Enterprise-grade financial intelligence. All rights reserved.
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>🇮🇳 Made for India</span>
                <span>•</span>
                <span>Built by{' '}
                  <a 
                    href="https://bwestudios.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="font-bold text-primary hover:text-primary/80 transition-colors"
                  >
                    BWE Studio
                  </a>
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Index;
